/**
 * Battle Chat Modal – same dimensions as menu modal.
 * Shows conversation balloons and a text input at the bottom.
 */
import React, { useState, useRef, useEffect } from "react";
import "./MenuModal.css";
import "./BattleChatModal.css";

function BattleChatModal({
  onClose,
  chatMessages = [],
  currentUserId,
  opponentName = "",
  langString = "en",
  onSendMessage,
  onMarkRead,
  isLoading = false,
}) {
  const [inputValue, setInputValue] = useState("");
  const listRef = useRef(null);
  const touchStartYRef = useRef(null);
  const textareaRef = useRef(null);

  const MIN_TEXTAREA_HEIGHT = 48;
  const MAX_TEXTAREA_HEIGHT = 120;

  const adjustTextareaHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const h = Math.min(Math.max(el.scrollHeight, MIN_TEXTAREA_HEIGHT), MAX_TEXTAREA_HEIGHT);
    el.style.height = `${h}px`;
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  useEffect(() => {
    onMarkRead?.();
  }, [onMarkRead]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;
    onSendMessage?.(trimmed);
    setInputValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
    }
  };

  const handleTouchStart = (e) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = (e) => {
    const startY = touchStartYRef.current;
    touchStartYRef.current = null;
    if (startY == null) return;
    const endY = e.changedTouches[0]?.clientY ?? startY;
    const deltaY = endY - startY;
    const scrollTop = listRef.current?.scrollTop ?? 0;
    if (deltaY > 60 && scrollTop <= 10) {
      onClose?.();
    }
  };

  return (
    <>
      <div
        className="battle-chat-overlay menu-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            e.stopPropagation();
            onClose();
          }
        }}
      />
      <div
        className="battle-chat-modal menu-modal"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          className="menu-close battle-chat-close"
          onClick={onClose}
          aria-label={langString === "nl" ? "Sluiten" : "Close"}
        >
          ✕
        </button>
        <h2 className="battle-chat-title">
          {langString === "nl" ? "Chat" : "Chat"}
        </h2>
        <div ref={listRef} className="battle-chat-messages">
          {chatMessages.length === 0 ? (
            <p className="battle-chat-empty">
              {langString === "nl"
                ? "Nog geen berichten. Stuur het eerste bericht!"
                : "No messages yet. Send the first message!"}
            </p>
          ) : (
            chatMessages.map((msg, idx) => {
              const isOwn = msg.senderId === currentUserId;
              return (
                <div
                  key={idx}
                  className={`battle-chat-bubble ${isOwn ? "battle-chat-bubble--own" : "battle-chat-bubble--other"}`}
                >
                  {!isOwn && (
                    <span className="battle-chat-bubble-sender">{opponentName || "Opponent"}</span>
                  )}
                  <span className="battle-chat-bubble-text">{msg.text}</span>
                </div>
              );
            })
          )}
        </div>
        <div className="battle-chat-form-spacer" aria-hidden="true" />
      </div>
      <form onSubmit={handleSubmit} className="battle-chat-form battle-chat-form--sticky">
        <textarea
          ref={textareaRef}
          className="battle-chat-input battle-chat-input--textarea"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder={langString === "nl" ? "Bericht..." : "Message..."}
          maxLength={500}
          disabled={isLoading}
          rows={1}
          aria-label={langString === "nl" ? "Chatbericht" : "Chat message"}
        />
        <button
          type="submit"
          className="battle-chat-send"
          disabled={!inputValue.trim() || isLoading}
          aria-label={langString === "nl" ? "Verstuur" : "Send"}
        >
          {langString === "nl" ? "Verstuur" : "Send"}
        </button>
      </form>
    </>
  );
}

export default BattleChatModal;
